/**
 * 免拍地址抽查（QC 模块 · 监控侧）
 *
 * 一页两 Tab：
 *   Tab1「抽查任务」        ← pod_exempt_spot_task + pod_exempt_spot_sample
 *   Tab2「生效地址数据监控」 ← pod_exempt_metric_daily
 *
 * 抽检池 = 生效中的豁免目标（pod_exempt_address ∪ pod_exempt_fence，
 * status = EFFECTIVE），非上报单。
 *
 * plan：docs/plans/feature-team-pod-edge-case-report.md
 */
import React, {useEffect, useMemo, useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {translateGraphQLError} from 'utils/graphqlUtils';
import {Select, Tooltip, message} from 'antd';
import {useTranslation} from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';
import MonitorTable from './components/MonitorTable';
import SpotTaskTable from './components/SpotTaskTable';
import SpotTaskDetailDrawer from './components/SpotTaskDetailDrawer';
import StatCards, {StatCardItem} from './components/StatCards';
import {useExemptMonitorQuery} from './hooks/useExemptMonitorQuery';
import {useSpotTaskQuery} from './hooks/useSpotTaskQuery';
import {useSpotTaskActions} from './hooks/useSpotTaskActions';
import {useSpotTaskDetail} from './hooks/useSpotTaskDetail';
import {
    ENABLE_PENALTY_DISPLAY,
    MONITOR_THRESHOLDS,
    getAvailableTaskTabs,
} from './config/exemptSpotCheckConfig';
import {
    ExemptSpotCheckTab,
    MonitorRow,
    SpotTaskRow,
    SpotTaskTab,
} from './types/ExemptSpotCheckType';
import {formatCount, formatRate} from './utils/metricFormat';
import {GET_SPOT_PERIODS, GetSpotPeriodsResponse} from './graphql/queries/spotTaskQueries';

/**
 * Tab 按钮样式，取值逐项对齐原型（sampTabs / taskFilters 两段内联 style）。
 *
 * 不用 antd Segmented：它自带滑块动画与 hover 背景，原型两者都没有，
 * 靠 CSS 覆盖会残留滑块位移，改成原生按钮反而更贴合且无副作用。
 *
 * @param pill true=分组（胶囊、选中实心紫底白字）；false=主 tab（方角、选中浅紫底紫字）
 */
const tabButtonStyle = (active: boolean, pill: boolean): React.CSSProperties => ({
    padding: pill ? '6px 14px' : '7px 16px',
    borderRadius: pill ? 999 : 6,
    fontSize: 13,
    cursor: 'pointer',
    border: `1px solid ${active ? '#722ed1' : '#e5e6eb'}`,
    background: active ? (pill ? '#722ed1' : '#faf5ff') : '#fff',
    color: active ? (pill ? '#fff' : '#722ed1') : '#4e5969',
});

const ExemptSpotCheckPage: React.FC = () => {
    const {t} = useTranslation();
    const [activeTab, setActiveTab] = useState<ExemptSpotCheckTab>(ExemptSpotCheckTab.TASKS);
    const [taskTab, setTaskTab] = useState<SpotTaskTab>(SpotTaskTab.PENDING);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const [detailVisible, setDetailVisible] = useState(false);

    /** 周期下拉：空串表示「全部周期」，默认选最新一期 */
    const [periodNo, setPeriodNo] = useState<string>('');
    const [periodOptions, setPeriodOptions] = useState<string[]>([]);

    const client = useApolloClient();
    const monitorQuery = useExemptMonitorQuery();
    const taskQuery = useSpotTaskQuery();
    const detailQuery = useSpotTaskDetail();
    const {loading: actionLoading, createTaskFromMonitor} = useSpotTaskActions();

    const isMonitor = activeTab === ExemptSpotCheckTab.MONITOR;

    // 首次挂载拉一次数据；后续由筛选/翻页/切 Tab 各自触发。
    // 任务列表要等期号到手才发：默认查最新一期，先查再改期会白跑一次全量。
    useEffect(() => {
        // 任务列表要等期号到手才发，先置加载态：否则这段时间表格显示「暂无数据」而非转圈
        taskQuery.beginLoading();
        // 监控 tab 默认不可见，首屏不预取；切到它时 handleMainTabChange 会拉
        client
            .query<GetSpotPeriodsResponse>({query: GET_SPOT_PERIODS, fetchPolicy: 'network-only'})
            .then((res) => {
                const periods = res.data?.podExemptSpotPeriods ?? [];
                setPeriodOptions(periods);
                const latest = periods[0] ?? '';
                setPeriodNo(latest);
                taskQuery.executeQuery({periodNo: latest || undefined}, 1, pageSize, SpotTaskTab.PENDING);
            })
            .catch((e) => {
                // 期号取不到时退化为不限周期，列表照常可用；但要提示，否则用户不知道下拉为何是空的
                setPeriodOptions([]);
                message.error(translateGraphQLError(e, 'exemptSpotCheck.messages.loadPeriodsFailed'));
                taskQuery.executeQuery({}, 1, pageSize, SpotTaskTab.PENDING);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅需挂载时执行一次，加入依赖会重复请求
    }, []);

    /** 所有任务查询共用：周期是页面级筛选，漏传会让翻页/切分组时退回全量 */
    const taskFilters = (period: string = periodNo) => (period ? {periodNo: period} : {});

    const handlePeriodChange = (value: string) => {
        setPeriodNo(value);
        setCurrentPage(1);
        taskQuery.executeQuery(taskFilters(value), 1, pageSize, taskTab);
    };

    const handleMainTabChange = (key: string) => {
        const nextTab = key as ExemptSpotCheckTab;
        setActiveTab(nextTab);
        setCurrentPage(1);
        if (nextTab === ExemptSpotCheckTab.MONITOR) {
            monitorQuery.executeQuery({}, 1, pageSize);
        } else {
            taskQuery.executeQuery(taskFilters(), 1, pageSize, taskTab);
        }
    };

    const handleTaskTabChange = (key: string) => {
        const nextTab = key as SpotTaskTab;
        setTaskTab(nextTab);
        setCurrentPage(1);
        taskQuery.executeQuery(taskFilters(), 1, pageSize, nextTab);
    };

    const handlePageChange = (page: number, nextPageSize: number) => {
        setCurrentPage(page);
        setPageSize(nextPageSize);
        if (isMonitor) {
            monitorQuery.executeQuery({}, page, nextPageSize);
        } else {
            taskQuery.executeQuery(taskFilters(), page, nextPageSize, taskTab);
        }
    };

    const handleCreateTask = async (row: MonitorRow) => {
        const ok = await createTaskFromMonitor({
            targetType: row.targetType,
            targetId: row.targetId,
            sourceDetail: row.triggerDetail,
        });
        if (ok) {
            taskQuery.executeQuery(taskFilters(), 1, pageSize, taskTab);
        }
    };

    const handleOpenDetail = async (row: SpotTaskRow) => {
        await detailQuery.loadDetail(row.id);
        setDetailVisible(true);
    };

    const handleConcluded = () => {
        setDetailVisible(false);
        detailQuery.clearDetail();
        taskQuery.executeQuery(taskFilters(), currentPage, pageSize, taskTab);
    };

    /** 监控看板统计卡 */
    const monitorStatItems: StatCardItem[] = useMemo(() => {
        const stats = monitorQuery.stats;
        return [
            {
                label: t('exemptSpotCheck.monitor.stats.monitoringCount'),
                value: formatCount(stats?.monitoringCount),
            },
            {
                label: t('exemptSpotCheck.monitor.stats.anomalyCount'),
                value: formatCount(stats?.anomalyCount),
                highlight: true,
            },
            {
                // 监控中地址近 7 日总单量，取数口径待数据侧确认
                label: t('exemptSpotCheck.monitor.stats.orderCount7d'),
                value: formatCount(stats?.orderCount7d, String(t('exemptSpotCheck.unit.order'))),
            },
            {
                label: t('exemptSpotCheck.monitor.stats.complaintCount7d'),
                value: formatCount(
                    stats?.complaintCount7d,
                    String(t('exemptSpotCheck.unit.complaint')),
                ),
            },
        ];
    }, [monitorQuery.stats, t]);

    /** 抽查任务统计卡 */
    const taskStatItems: StatCardItem[] = useMemo(() => {
        const stats = taskQuery.stats;
        return [
            {
                label: t('exemptSpotCheck.task.stats.pendingCount'),
                value: formatCount(stats?.pendingCount),
            },
            {
                label: t('exemptSpotCheck.task.stats.monitorAnomalyCount'),
                value: formatCount(stats?.monitorAnomalyCount),
            },
            {
                label: t('exemptSpotCheck.task.stats.randomSampleCount'),
                value: formatCount(stats?.randomSampleCount),
            },
            {
                // 原型为「下线/重罚 14/6」，罚款链路不在方案范围，
                // ENABLE_PENALTY_DISPLAY 开关关闭时只显示下线数
                label: ENABLE_PENALTY_DISPLAY
                    ? t('exemptSpotCheck.task.stats.offlineAndPenalty')
                    : t('exemptSpotCheck.task.stats.offlineCount'),
                value: formatCount(stats?.offlineCount),
                highlight: true,
            },
        ];
    }, [taskQuery.stats, t]);

    /** tab 行右端的常驻说明：监控页给阈值口径，任务页给入池来源 */
    const tabHint = isMonitor
        ? String(
              t('exemptSpotCheck.monitor.thresholdHint', {
                  highRiskRate: formatRate(MONITOR_THRESHOLDS.highRiskDeltaRate, 0),
                  attentionRate: formatRate(MONITOR_THRESHOLDS.attentionDeltaRate, 0),
              }),
          )
        : String(t('exemptSpotCheck.task.sourceHint'));

    const taskTabItems = useMemo(
        () =>
            getAvailableTaskTabs().map((tab) => ({
                value: tab,
                label: `${t(`exemptSpotCheck.taskTab.${tab}`)} ${taskQuery.tabCounts[tab] ?? 0}`,
            })),
        [taskQuery.tabCounts, t],
    );

    const total = isMonitor ? monitorQuery.total : taskQuery.total;

    return (
        <div className="rounded-lg bg-white px-3 pb-3 pt-3 shadow-sm">
            <div className="mb-3 flex items-start justify-between">
                <div>
                    <div className="text-lg font-semibold">{t('exemptSpotCheck.title')}</div>
                    <div className="mt-1 text-xs text-gray-500">{t('exemptSpotCheck.subtitle')}</div>
                </div>
            </div>

            <StatCards
                items={isMonitor ? monitorStatItems : taskStatItems}
                loading={isMonitor ? monitorQuery.loading : taskQuery.loading}
            />

            {/* 两组 tab 样式按原型区分层级：主 tab 描边、分组实心，避免二者同形看不出从属 */}

            <div className="mb-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    {[
                        {value: ExemptSpotCheckTab.TASKS, label: t('exemptSpotCheck.tabs.tasks')},
                        {value: ExemptSpotCheckTab.MONITOR, label: t('exemptSpotCheck.tabs.monitor')},
                    ].map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            onClick={() => handleMainTabChange(item.value)}
                            style={tabButtonStyle(activeTab === item.value, false)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
                {/* 说明贴 tab 行右端（原型样式）；宽度不够时截断，悬浮出全文，不换行挤高 tab 行。
                    span 宽度必须贴合文字：撑满剩余空间会让 Tooltip 锚到空白区中心，气泡与文字错位 */}
                <Tooltip title={tabHint}>
                    <span className="min-w-0 shrink truncate text-xs text-gray-400">{tabHint}</span>
                </Tooltip>
            </div>

            {isMonitor ? (
                <MonitorTable
                    rows={monitorQuery.rows}
                    loading={monitorQuery.loading}
                    creating={actionLoading}
                    onCreateTask={handleCreateTask}
                />
            ) : (
                <>
                    {/* 分组用胶囊按钮而非下划线 tab：与主 tab 区分层级，二者同为下划线会看不出从属 */}
                    <div className="mb-3 flex items-center justify-between gap-4">
                        {/* 分组用胶囊按钮而非下划线 tab：与主 tab 区分层级，二者同为下划线会看不出从属 */}
                        <div className="flex items-center gap-2">
                            {taskTabItems.map((item) => (
                                <button
                                    key={item.value}
                                    type="button"
                                    onClick={() => handleTaskTabChange(item.value)}
                                    style={tabButtonStyle(taskTab === item.value, true)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        {/* 默认最新一期：抽查按期开展，跨期后仍要能回看历史期 */}
                        <Select
                            value={periodNo}
                            onChange={handlePeriodChange}
                            options={[
                                {value: '', label: t('exemptSpotCheck.task.allPeriods')},
                                ...periodOptions.map((item) => ({value: item, label: item})),
                            ]}
                            style={{width: 160}}
                        />
                    </div>
                    <SpotTaskTable
                        rows={taskQuery.rows}
                        loading={taskQuery.loading}
                        tab={taskTab}
                        onOpenDetail={handleOpenDetail}
                    />
                </>
            )}

            <TablePaginationBar
                total={total}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                // 监控 tab 的汇总口径是「监控中 / 触发异常」，与通用的「合计 N 条」不同，
                // 换掉左侧文案而不是另起一行，避免出现两个「合计」
                totalText={
                    isMonitor
                        ? t('exemptSpotCheck.monitor.summary', {
                              monitoringCount: formatCount(monitorQuery.stats?.monitoringCount),
                              anomalyCount: formatCount(monitorQuery.stats?.anomalyCount),
                          })
                        : undefined
                }
            />

            <SpotTaskDetailDrawer
                visible={detailVisible}
                detail={detailQuery.detail}
                onClose={() => {
                    setDetailVisible(false);
                    detailQuery.clearDetail();
                }}
                onConcluded={handleConcluded}
            />
        </div>
    );
};

export default ExemptSpotCheckPage;
